<script setup lang="ts">
import PdsAuthShell from '@/components/pds/PdsAuthShell.vue';
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsField from '@/components/pds/PdsField.vue';
import PdsHeading from '@/components/pds/PdsHeading.vue';
import { authService } from '@/stores/auth/auth.service';
import { useAuthStore } from '@/stores/auth/auth.slice';
import { preloadImages } from '@/utils/preloadImages';
import { uuidv4 } from '@/utils/uuid';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

const email = ref('');
const password = ref('');
// Les trois illustrations d'état sont chargées d'emblée : celle d'un réseau coupé ne peut plus se demander à ce moment-là.
preloadImages([
  '/image/pds-link.png',
  '/image/pds-connected.png',
  '/image/pds-offline.png',
]);

const isLoading = ref(false);
const status = ref<'idle' | 'refused' | 'offline' | 'connected'>('idle');

const STORE_LOOKUP_TIMEOUT_MS = 3_000;

const storeLine = ref('');
const primaryButton = useTemplateRef<InstanceType<typeof PdsButton>>('primary');
const account = computed(
  () => authStore.decodedJwt.accessToken?.user_email ?? email.value,
);
const connectedLine = computed(() => storeLine.value || account.value);

// Le nom du commerce est un confort : sans lui, l'écran affiche le compte.
const loadStoreLine = async () => {
  const storeId = authStore.decodedJwt.accessToken?.restaurant_ref;
  if (!storeId) return '';

  const abortCrt = new AbortController();
  const timeout = setTimeout(() => abortCrt.abort(), STORE_LOOKUP_TIMEOUT_MS);
  try {
    const { data } = await authService.getStore(storeId, {
      requestId: uuidv4(),
      signal: abortCrt.signal,
      jwttoken: authStore.tokens.accessToken,
    });
    return [data?.designation, data?.metadata?.city]
      .filter(Boolean)
      .join(' · ');
  } catch {
    return '';
  } finally {
    clearTimeout(timeout);
  }
};

const focusPrimaryButton = async () => {
  await nextTick();
  primaryButton.value?.$el.focus();
};

const clearRefused = () => {
  if (status.value === 'refused') status.value = 'idle';
};

const handleContinue = () => {
  router.push((route.query.redirect as string | undefined) ?? '/');
};

const handleLogin = async () => {
  isLoading.value = true;
  let result: Awaited<ReturnType<typeof authStore.login>>;
  try {
    result = await authStore.login({
      emailOrPhone: email.value,
      password: password.value,
    });
    if (result.data?.data) storeLine.value = await loadStoreLine();
  } finally {
    isLoading.value = false;
  }

  if (result.data?.data) {
    status.value = 'connected';
    await focusPrimaryButton();
    return;
  }
  // Un échec réseau fait lever le service : aucune réponse, donc `data` absent.
  // Un refus serveur répond en JSON sans jeton.
  if (result.data) {
    password.value = '';
    status.value = 'refused';
  } else {
    status.value = 'offline';
    await focusPrimaryButton();
  }
};
</script>

<template>
  <PdsAuthShell>
    <template v-if="status === 'offline'">
      <img
        src="/image/pds-offline.png"
        alt=""
        class="size-[88px] object-cover"
      />
      <PdsHeading title="Nextep est injoignable" alert>
        La tablette n’a pas pu joindre Nextep. Vérifiez le réseau, puis
        réessayez.
      </PdsHeading>
      <PdsButton ref="primary" :loading="isLoading" @click="handleLogin">
        Réessayer
      </PdsButton>
    </template>

    <template v-else-if="status === 'connected'">
      <img
        src="/image/pds-connected.png"
        alt=""
        class="size-[88px] object-cover"
      />
      <PdsHeading title="Tablette connectée" ink>
        {{ connectedLine }}
      </PdsHeading>
      <PdsButton ref="primary" @click="handleContinue">Continuer</PdsButton>
      <p class="text-encre-douce w-full text-[12px] leading-[17px]">
        Chaque préparateur entre ensuite son code. Le journal retient qui a fait
        quoi.
      </p>
    </template>

    <form
      v-else
      class="flex w-full flex-col items-center gap-5"
      @submit.prevent="handleLogin"
    >
      <img src="/image/pds-link.png" alt="" class="size-[88px] object-cover" />
      <PdsHeading title="Connecter la tablette" :alert="status === 'refused'">
        <template v-if="status === 'refused'">
          Adresse ou mot de passe refusé. Vérifiez les deux, puis réessayez.
        </template>
        <template v-else>
          Entrez le compte Nextep du commerce. Cette tablette n’aura à le faire
          qu’une fois.
        </template>
      </PdsHeading>

      <div class="flex w-full flex-col gap-4">
        <PdsField
          id="login-email"
          v-model="email"
          type="email"
          label="Adresse du commerce"
          autocomplete="email"
          placeholder="nom@commerce.be"
          required
          :invalid="status === 'refused'"
          @update:model-value="clearRefused"
        />
        <PdsField
          id="login-psw"
          v-model="password"
          type="password"
          label="Mot de passe"
          autocomplete="current-password"
          required
          :invalid="status === 'refused'"
          @update:model-value="clearRefused"
        />
      </div>

      <div class="flex w-full flex-col gap-4">
        <p class="flex flex-wrap gap-x-2 text-[14px] leading-5">
          <span class="text-encre-douce">Mot de passe oublié ?</span>
          <RouterLink to="/recove" class="text-encre font-semibold">
            Réinitialiser le mot de passe
          </RouterLink>
        </p>
        <PdsButton type="submit" :loading="isLoading">Se connecter</PdsButton>
      </div>
    </form>
  </PdsAuthShell>
</template>
